import { createSlice } from '@reduxjs/toolkit';
import * as robotScheduleActionTypes from './robotScheduleActions.types';
import type { Optional } from 'lib/types/Optional';
import type {
  AvailableMap,
  ScheduleOccurrence,
  ScheduleRoute,
  ScheduleRoutine,
} from 'app/modules/machine-inventory/ScheduleClient';
import type {
  AssociateDevicesToMapResponse,
  CleaningScheduleV2,
  Map,
  MapGroup,
  QueryCleaningScheduleOccurrencesArgs,
} from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';

export interface RobotScheduleRoutines {
  isLoading: boolean;
  data: Optional<ScheduleRoutine[]>;
  error: Optional<unknown>;
}

export interface RobotScheduleRoutes {
  isLoading: boolean;
  data: Optional<ScheduleRoute[]>;
  error: Optional<unknown>;
}

export interface RobotScheduleMaps {
  isLoading: boolean;
  data: Optional<Map[]>;
}

export interface RobotScheduleOccurrences {
  isLoading: boolean;
  isRefreshing: boolean;
  data: Optional<ScheduleOccurrence[]>;
  error: Optional<unknown>;
  lastRequest: Optional<QueryCleaningScheduleOccurrencesArgs>;
  lastSyncedAt: Optional<string>;
}

export interface RobotScheduleCreate {
  isLoading: boolean;
  data: Optional<CleaningScheduleV2>;
}

export interface RobotScheduleUpdate {
  isLoading: boolean;
  data: Optional<CleaningScheduleV2>;
}

interface RobotScheduleDelete {
  isLoading: boolean;
  data: Optional<CleaningScheduleV2>;
}

export interface RobotScheduleAvailableMaps {
  isLoading: boolean;
  data: Optional<AvailableMap[]>;
}

export interface RobotScheduleAvailableMapGroups {
  isLoading: boolean;
  data: Optional<MapGroup[]>;
}

export interface RobotScheduleAssociateDevicesToMap {
  isLoading: boolean;
  data: Optional<AssociateDevicesToMapResponse>;
  error: Optional<unknown>;
}

export interface RobotScheduleState {
  routines: RobotScheduleRoutines;
  routes: RobotScheduleRoutes;
  maps: RobotScheduleMaps;
  occurrences: RobotScheduleOccurrences;
  create: RobotScheduleCreate;
  update: RobotScheduleUpdate;
  delete: RobotScheduleDelete;
  availableMaps: RobotScheduleAvailableMaps;
  availableMapGroups: RobotScheduleAvailableMapGroups;
  associateDevicesToMap: RobotScheduleAssociateDevicesToMap;
}

export const initialState: RobotScheduleState = {
  routines: {
    isLoading: false,
    data: null,
    error: null,
  },
  routes: {
    isLoading: false,
    data: null,
    error: null,
  },
  maps: {
    isLoading: false,
    data: null,
  },
  occurrences: {
    isLoading: false,
    isRefreshing: false,
    data: null,
    error: null,
    lastRequest: null,
    lastSyncedAt: null,
  },
  create: {
    isLoading: false,
    data: null,
  },
  update: {
    isLoading: false,
    data: null,
  },
  delete: {
    isLoading: false,
    data: null,
  },
  availableMaps: {
    isLoading: false,
    data: null,
  },
  availableMapGroups: {
    isLoading: false,
    data: null,
  },
  associateDevicesToMap: {
    isLoading: false,
    data: null,
    error: null,
  },
};

const robotScheduleSlice = createSlice({
  name: 'robot-schedule',
  initialState,
  reducers: {
    robotScheduleListRoutinesRequest: (
      state,
      _action: robotScheduleActionTypes.RobotScheduleListRoutinesRequestAction
    ) => {
      state.routines.isLoading = true;
      state.routines.error = null;
      return state;
    },
    robotScheduleListRoutinesSuccess: (
      state,
      action: robotScheduleActionTypes.RobotScheduleListRoutinesSuccessAction
    ) => {
      state.routines.isLoading = false;
      state.routines.data = action.payload.routines.nodes.filter(
        (routine): routine is ScheduleRoutine => routine !== null
      );
      return state;
    },
    robotScheduleListRoutinesError: (state, action: robotScheduleActionTypes.RobotScheduleListRoutinesErrorAction) => {
      state.routines.isLoading = false;
      state.routines.error = action.payload.error;
      return state;
    },
    robotScheduleListRoutinesReset: state => {
      state.routines = initialState.routines;
      return state;
    },

    robotScheduleListRoutesRequest: (state, _action: robotScheduleActionTypes.RobotScheduleListRoutesRequestAction) => {
      state.routes.isLoading = true;
      state.routes.error = null;
      return state;
    },
    robotScheduleListRoutesSuccess: (state, action: robotScheduleActionTypes.RobotScheduleListRoutesSuccessAction) => {
      state.routes.isLoading = false;
      state.routes.data = action.payload.routes.nodes.filter((route): route is ScheduleRoute => route !== null);
      return state;
    },
    robotScheduleListRoutesError: (state, action: robotScheduleActionTypes.RobotScheduleListRoutesErrorAction) => {
      state.routes.isLoading = false;
      state.routes.error = action.payload.error;
      return state;
    },
    robotScheduleListRoutesReset: state => {
      state.routes = initialState.routes;
      return state;
    },

    listMapsRequest: (state, _action: robotScheduleActionTypes.ListMapsRequestAction) => {
      state.maps.isLoading = true;
      return state;
    },
    listMapsSuccess: (state, action: robotScheduleActionTypes.ListMapsSuccessAction) => {
      state.maps.isLoading = false;
      state.maps.data = action.payload.maps.nodes;
      return state;
    },
    listMapsError: (state, _action: robotScheduleActionTypes.ListMapsErrorAction) => {
      state.maps.isLoading = false;
      state.maps.data = null;
      return state;
    },
    listMapsReset: state => {
      state.maps = initialState.maps;
      return state;
    },

    robotScheduleListOccurrencesRequest: (
      state,
      action: robotScheduleActionTypes.RobotScheduleListOccurrencesRequestAction
    ) => {
      state.occurrences.isLoading = true;
      state.occurrences.isRefreshing = false;
      state.occurrences.error = null;
      state.occurrences.data = null;
      // A different robot or period is a new sync scope; don't inherit the previous sync time.
      if (
        state.occurrences.lastRequest?.materialNumber !== action.payload.materialNumber ||
        state.occurrences.lastRequest?.serialNumber !== action.payload.serialNumber ||
        state.occurrences.lastRequest?.period.startAt !== action.payload.period.startAt ||
        state.occurrences.lastRequest?.period.endAt !== action.payload.period.endAt
      ) {
        state.occurrences.lastSyncedAt = null;
      }
      state.occurrences.lastRequest = action.payload;
      return state;
    },
    robotScheduleListOccurrencesSuccess: (
      state,
      action: robotScheduleActionTypes.RobotScheduleListOccurrencesSuccessAction
    ) => {
      // A refresh that landed first already holds newer data; this older response must not overwrite it.
      if (!state.occurrences.isLoading) {
        return state;
      }
      state.occurrences.isLoading = false;
      state.occurrences.data = action.payload.cleaningScheduleOccurrences;
      state.occurrences.lastSyncedAt = new Date().toISOString();
      return state;
    },
    robotScheduleListOccurrencesError: (
      state,
      action: robotScheduleActionTypes.RobotScheduleListOccurrencesErrorAction
    ) => {
      if (!state.occurrences.isLoading) {
        return state;
      }
      state.occurrences.isLoading = false;
      state.occurrences.error = action.payload.error;
      state.occurrences.data = null;
      return state;
    },

    // Silent refetch: keeps the current data and loading flag so the calendar doesn't flicker.
    robotScheduleRefreshOccurrences: (
      state,
      _action: robotScheduleActionTypes.RobotScheduleRefreshOccurrencesAction
    ) => {
      state.occurrences.isRefreshing = true;
      return state;
    },

    // Clears the refreshing flag when a refresh is dropped or fails, leaving the data as it is.
    robotScheduleRefreshOccurrencesDone: (
      state,
      _action: robotScheduleActionTypes.RobotScheduleRefreshOccurrencesDoneAction
    ) => {
      state.occurrences.isRefreshing = false;
      return state;
    },

    robotScheduleRefreshOccurrencesSuccess: (
      state,
      action: robotScheduleActionTypes.RobotScheduleRefreshOccurrencesSuccessAction
    ) => {
      // Ends a pending list request too: this snapshot is newer than whatever it will return.
      state.occurrences.isLoading = false;
      state.occurrences.isRefreshing = false;
      state.occurrences.error = null;
      state.occurrences.data = action.payload.cleaningScheduleOccurrences;
      state.occurrences.lastSyncedAt = new Date().toISOString();
      return state;
    },

    followScheduleChanges: (state, _action: robotScheduleActionTypes.FollowScheduleChangesAction) => state,
    stopFollowingScheduleChanges: state => state,

    createScheduleRequest: (state, _action: robotScheduleActionTypes.CreateScheduleRequestAction) => {
      state.create.isLoading = true;
      state.create.data = null;
      return state;
    },
    createScheduleSuccess: (state, action: robotScheduleActionTypes.CreateScheduleSuccessAction) => {
      state.create.isLoading = false;
      state.create.data = action.payload;
      return state;
    },
    createScheduleError: (state, _action: robotScheduleActionTypes.CreateScheduleErrorAction) => {
      state.create.isLoading = false;
      state.create.data = null;
      return state;
    },
    createScheduleReset: state => {
      state.create = initialState.create;
    },

    updateScheduleRequest: (state, _action: robotScheduleActionTypes.UpdateScheduleRequestAction) => {
      state.update.isLoading = true;
      state.update.data = null;
      return state;
    },
    updateScheduleSuccess: (state, action: robotScheduleActionTypes.UpdateScheduleSuccessAction) => {
      state.update.isLoading = false;
      state.update.data = action.payload;
      return state;
    },
    updateScheduleError: (state, _action: robotScheduleActionTypes.UpdateScheduleErrorAction) => {
      state.update.isLoading = false;
      state.update.data = null;
      return state;
    },
    updateScheduleReset: state => {
      state.update = initialState.update;
    },

    deleteScheduleRequest: (state, _action: robotScheduleActionTypes.DeleteScheduleRequestAction) => {
      state.delete.isLoading = true;
      state.delete.data = null;
      return state;
    },
    deleteScheduleSuccess: (state, action: robotScheduleActionTypes.DeleteScheduleSuccessAction) => {
      state.delete.isLoading = false;
      state.delete.data = action.payload;
      return state;
    },
    deleteScheduleError: (state, _action: robotScheduleActionTypes.DeleteScheduleErrorAction) => {
      state.delete.isLoading = false;
      state.delete.data = null;
      return state;
    },
    deleteScheduleReset: state => {
      state.delete = initialState.delete;
      return state;
    },

    listAvailableMapsRequest: (state, _action: robotScheduleActionTypes.ListAvailableMapsRequestAction) => {
      state.availableMaps.isLoading = true;
      return state;
    },
    listAvailableMapsSuccess: (state, action: robotScheduleActionTypes.ListAvailableMapsSuccessAction) => {
      state.availableMaps.isLoading = false;
      state.availableMaps.data = action.payload.maps.nodes;
      return state;
    },
    listAvailableMapsError: (state, _action: robotScheduleActionTypes.ListAvailableMapsErrorAction) => {
      state.availableMaps.isLoading = false;
      state.availableMaps.data = null;
      return state;
    },
    listAvailableMapsReset: state => {
      state.availableMaps = initialState.availableMaps;
      return state;
    },

    listAvailableMapGroupsRequest: (state, _action: robotScheduleActionTypes.ListAvailableMapGroupsRequestAction) => {
      state.availableMapGroups.isLoading = true;
      return state;
    },
    listAvailableMapGroupsSuccess: (state, action: robotScheduleActionTypes.ListAvailableMapGroupsSuccessAction) => {
      state.availableMapGroups.isLoading = false;
      state.availableMapGroups.data = action.payload.mapGroups.nodes;
      return state;
    },
    listAvailableMapGroupsError: (state, _action: robotScheduleActionTypes.ListAvailableMapGroupsErrorAction) => {
      state.availableMapGroups.isLoading = false;
      state.availableMapGroups.data = null;
      return state;
    },
    listAvailableMapGroupsReset: state => {
      state.availableMapGroups = initialState.availableMapGroups;
      return state;
    },

    associateDevicesToMapRequest: (state, _action: robotScheduleActionTypes.AssociateDevicesToMapRequestAction) => {
      state.associateDevicesToMap.isLoading = true;
      state.associateDevicesToMap.error = null;
      return state;
    },
    associateDevicesToMapSuccess: (state, action: robotScheduleActionTypes.AssociateDevicesToMapSuccessAction) => {
      state.associateDevicesToMap.isLoading = false;
      state.associateDevicesToMap.data = action.payload;
      return state;
    },
    associateDevicesToMapError: (state, action: robotScheduleActionTypes.AssociateDevicesToMapErrorAction) => {
      state.associateDevicesToMap.isLoading = false;
      state.associateDevicesToMap.error = action.payload.error;
      return state;
    },
    associateDevicesToMapReset: state => {
      state.associateDevicesToMap = initialState.associateDevicesToMap;
      return state;
    },
  },
});
export const RobotScheduleActions = {
  ...robotScheduleSlice.actions,
};
export const robotScheduleReducer = robotScheduleSlice.reducer;
